// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	HOLIDAY_PACK_IDS,
	type HolidayPackId,
	isHolidayPackEnabled,
	isHolidayPreseasonEnabled,
} from '@app/features/holiday/state/HolidayPreferences';

export type HolidaySeasonPhase = 'off' | 'preseason' | 'peak';

/**
 * Season boundaries for one occurrence, resolved per year in local time.
 * Preseason runs [preseasonStart, peakStart); peak runs [peakStart, seasonEnd).
 * A window may end in the following calendar year.
 */
export interface HolidaySeasonWindow {
	preseasonStart: (year: number) => Date;
	peakStart: (year: number) => Date;
	seasonEnd: (year: number) => Date;
}

const SEASON_WINDOWS: Record<HolidayPackId, HolidaySeasonWindow> = {
	halloween: {
		preseasonStart: (year) => new Date(year, 8, 1),
		peakStart: (year) => new Date(year, 9, 1),
		seasonEnd: (year) => new Date(year, 10, 1),
	},
	festive: {
		preseasonStart: (year) => new Date(year, 10, 20),
		peakStart: (year) => new Date(year, 11, 1),
		// Exclusive: peak through Dec 26.
		seasonEnd: (year) => new Date(year, 11, 27),
	},
	newyear: {
		preseasonStart: (year) => new Date(year, 11, 31, 12, 0, 0),
		peakStart: (year) => new Date(year, 11, 31, 12, 0, 0),
		// Exclusive: off at 1 AM Jan 1.
		seasonEnd: (year) => new Date(year + 1, 0, 1, 1, 0, 0),
	},
};

function contains(start: Date, end: Date, now: Date): boolean {
	const t = now.getTime();
	return t >= start.getTime() && t < end.getTime();
}

/** Boundaries of the single occurrence that contains `now`, with the phase it falls in. */
export interface ResolvedHolidaySeasonWindow {
	phase: Exclude<HolidaySeasonPhase, 'off'>;
	preseasonStart: Date;
	peakStart: Date;
	seasonEnd: Date;
}

function resolveWindow(window: HolidaySeasonWindow, now: Date): ResolvedHolidaySeasonWindow | null {
	const years = [now.getFullYear(), now.getFullYear() - 1];
	for (const year of years) {
		const preseasonStart = window.preseasonStart(year);
		const peakStart = window.peakStart(year);
		const seasonEnd = window.seasonEnd(year);
		if (preseasonStart.getTime() > peakStart.getTime() || peakStart.getTime() > seasonEnd.getTime()) {
			throw new Error('Holiday season window is inverted');
		}
		if (contains(peakStart, seasonEnd, now)) return {phase: 'peak', preseasonStart, peakStart, seasonEnd};
		if (contains(preseasonStart, peakStart, now)) return {phase: 'preseason', preseasonStart, peakStart, seasonEnd};
	}
	return null;
}

/** Calendar phase for an arbitrary window. Exported so tests can cover year-crossing without a second pack. */
export function phaseForWindow(window: HolidaySeasonWindow, now: Date): HolidaySeasonPhase {
	return resolveWindow(window, now)?.phase ?? 'off';
}

/**
 * Resolved boundaries for the occurrence containing `now`, or null out of season.
 * Calendar only, no preferences. Effects that ramp across a season read their waypoints from here
 * instead of hardcoding one pack's dates.
 */
export function resolveHolidaySeasonWindow(
	packId: HolidayPackId,
	now: Date = new Date(),
): ResolvedHolidaySeasonWindow | null {
	return resolveWindow(SEASON_WINDOWS[packId], now);
}

/** Calendar only, no preferences. */
export function holidaySeasonPhase(packId: HolidayPackId, now: Date = new Date()): HolidaySeasonPhase {
	return phaseForWindow(SEASON_WINDOWS[packId], now);
}

/** Calendar plus preferences: pack off, or preseason with preseason events off, becomes `'off'`. */
export function enabledHolidaySeasonPhase(packId: HolidayPackId, now: Date = new Date()): HolidaySeasonPhase {
	const calendar = holidaySeasonPhase(packId, now);
	if (calendar === 'off') return 'off';
	if (!isHolidayPackEnabled(packId)) return 'off';
	if (calendar === 'preseason' && !isHolidayPreseasonEnabled(packId)) return 'off';
	return calendar;
}

export function isHolidaySeasonEnabled(packId: HolidayPackId, now: Date = new Date()): boolean {
	return enabledHolidaySeasonPhase(packId, now) !== 'off';
}

/** True when any pack's calendar window is preseason or peak. */
export function isAnyHolidaySeason(now: Date = new Date()): boolean {
	return HOLIDAY_PACK_IDS.some((packId) => holidaySeasonPhase(packId, now) !== 'off');
}

/** Boundaries of the occurrence starting after `now`. Used once a window has already passed. */
function nextOccurrence(window: HolidaySeasonWindow, now: Date): ResolvedHolidaySeasonWindow {
	for (const year of [now.getFullYear(), now.getFullYear() + 1]) {
		const preseasonStart = window.preseasonStart(year);
		if (preseasonStart.getTime() > now.getTime()) {
			return {
				phase: 'preseason',
				preseasonStart,
				peakStart: window.peakStart(year),
				seasonEnd: window.seasonEnd(year),
			};
		}
	}
	const year = now.getFullYear();
	return {
		phase: 'preseason',
		preseasonStart: window.preseasonStart(year),
		peakStart: window.peakStart(year),
		seasonEnd: window.seasonEnd(year),
	};
}

/**
 * Occurrence to describe to the user: the live one if a season is running, else the next one.
 * Resolving through this rather than `now.getFullYear()` matters for windows that cross the year —
 * on Jan 1 during the new year peak, the bare year would name next December's occurrence.
 */
function displayOccurrence(packId: HolidayPackId, now: Date): ResolvedHolidaySeasonWindow {
	const window = SEASON_WINDOWS[packId];
	return resolveWindow(window, now) ?? nextOccurrence(window, now);
}

/**
 * Pack that is in season now, otherwise the pack whose next preseasonStart is soonest.
 */
export function currentOrUpcomingHolidayPackId(
	now: Date = new Date(),
	packIds: ReadonlyArray<HolidayPackId> = HOLIDAY_PACK_IDS,
): HolidayPackId {
	for (const packId of packIds) {
		if (holidaySeasonPhase(packId, now) !== 'off') return packId;
	}
	let soonestPackId: HolidayPackId = packIds[0] ?? HOLIDAY_PACK_IDS[0];
	let soonestTime = Number.POSITIVE_INFINITY;
	for (const packId of packIds) {
		const start = nextOccurrence(SEASON_WINDOWS[packId], now).preseasonStart.getTime();
		if (start > now.getTime() && start < soonestTime) {
			soonestTime = start;
			soonestPackId = packId;
		}
	}
	return soonestPackId;
}

/**
 * Next instant at or after `now` when this pack's phase can change.
 *
 * Derived from the window boundaries rather than assuming midnight: `newyear` starts at noon and
 * ends at 1 AM, so a midnight tick would miss its window entirely.
 */
export function nextHolidayPhaseBoundary(packId: HolidayPackId, now: Date = new Date()): Date {
	const window = SEASON_WINDOWS[packId];
	const current = resolveWindow(window, now);
	// Out of season, the only edge ahead is the next occurrence opening. In season, it is the far
	// edge of whichever phase we are standing in.
	if (current == null) return nextOccurrence(window, now).preseasonStart;
	return current.phase === 'preseason' ? current.peakStart : current.seasonEnd;
}

/** Earliest `nextHolidayPhaseBoundary` across every pack. */
export function nextAnyHolidayPhaseBoundary(now: Date = new Date()): Date {
	let soonest = Number.POSITIVE_INFINITY;
	for (const packId of HOLIDAY_PACK_IDS) {
		const time = nextHolidayPhaseBoundary(packId, now).getTime();
		if (time < soonest) soonest = time;
	}
	return new Date(soonest);
}

export function holidayPreseasonStart(packId: HolidayPackId, now: Date = new Date()): Date {
	return displayOccurrence(packId, now).preseasonStart;
}

/** Peak window as inclusive calendar days. `seasonEnd` may fall mid-day. */
export function holidayPeakRange(packId: HolidayPackId, now: Date = new Date()): {start: Date; endInclusive: Date} {
	const {peakStart, seasonEnd} = displayOccurrence(packId, now);
	const lastPeak = new Date(seasonEnd.getTime() - 1);
	return {
		start: peakStart,
		endInclusive: new Date(lastPeak.getFullYear(), lastPeak.getMonth(), lastPeak.getDate()),
	};
}
